<template>
    <q-card>
        <div v-if="defaultTimePicker === 'analog'">
            <q-time
                :model-value="timePicker.analogTime"
                :with-seconds="timePicker.withSecondsOption"
                format24h
                class="full-width"
                flat
                @update:model-value="
                    val => {
                        this.timePicker.analogTime = val;
                        timeChanged();
                    }
                "
                :minute-options="getMinutesOptions"
            />
        </div>
        <div v-else>
            <div class="label bg-primary q-pa-md">
                <div class="input-time-picker">
                    <q-input
                        input-class="text-bold text-center"
                        :input-style="'font-size: 1.5rem;'"
                        v-model="timeInput"
                        @keydown="preventInvalidTimeInput"
                        @update:model-value="handleTimeInput"
                        :mask="withSeconds ? '##:##:##' : '##:##'"
                        dark
                        outlined
                        dense
                        :error="errorMessage !== null"
                    >
                        <template v-slot:error>
                            <div class="text-light-red">
                                {{ errorMessage }}
                            </div>
                        </template>
                    </q-input>
                </div>
            </div>
            <div class="row justify-center">
                <vue-scroll-picker
                    v-if="isRendered"
                    :options="getHoursOptions"
                    :model-value="timePicker.digitalTime.hours"
                    @update:model-value="
                        val => {
                            this.timePicker.digitalTime.hours = val;
                            timeChanged();
                        }
                    "
                    class="scroll-picker"
                />
                <vue-scroll-picker
                    v-if="isRendered"
                    :options="getMinutesOptions"
                    :model-value="timePicker.digitalTime.minutes"
                    @update:model-value="
                        val => {
                            this.timePicker.digitalTime.minutes = val;
                            timeChanged();
                        }
                    "
                    class="scroll-picker"
                />
                <vue-scroll-picker
                    v-if="isRendered && withSeconds"
                    :options="getSecondsOptions"
                    :model-value="timePicker.digitalTime.seconds"
                    @update:model-value="
                        val => {
                            this.timePicker.digitalTime.seconds = val;
                            timeChanged();
                        }
                    "
                    class="scroll-picker"
                />
            </div>
        </div>
    </q-card>
</template>

<script>
import { VueScrollPicker } from 'vue-scroll-picker';
import 'vue-scroll-picker/style.css';
import { mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'TimePicker',
    components: { VueScrollPicker },
    emits: ['update:modelValue'],
    props: {
        modelValue: {
            type: String,
            required: false,
            default: '00:00:00'
        },
        withSeconds: {
            type: Boolean,
            required: false,
            default: false
        },
        hourInterval: { type: String, required: false, default: '1' },
        minuteInterval: {
            type: String,
            required: false,
            default: '1'
        },
        disable: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data() {
        return {
            timePicker: {
                digitalTime: {
                    hours: '00',
                    minutes: '00',
                    seconds: '00'
                },
                analogTime: '00:00:00',
                withSecondsOption: this.withSeconds,
                withHourInterval: this.hourInterval,
                withMinuteInterval: this.minuteInterval,
                disabled: this.disable
            },
            isRendered: false,
            timeInput: '',
            errorMessage: null
        };
    },
    async created() {
        // if user is using analog time picker, set the analog time
        if (this.user['default_time_picker'] === 'analog') {
            this.timePicker.analogTime = this.modelValue;
        } else {
            // if user is using digital time picker, set the digital time
            const time = this.modelValue.split(':');
            this.timePicker.digitalTime.hours = time[0];
            this.timePicker.digitalTime.minutes = time[1];
            this.timePicker.digitalTime.seconds = time[2];
            this.timeInput = this.modelValue;
        }

        /**
         * Sleep for 400ms to render the vue-scroll-picker
         * This is a 'hack' to get the vue-scroll-picker to render as identified in this
         * this GitHub issue https://github.com/wan2land/vue-scroll-picker/issues/544
         * The vue-scroll-picker is not rendering properly when the component is first loaded.
         * This may be due to the delay when the component is presetting the values of each scroll picker on render.
         * This is a temporary fix until the vue-scroll-picker is updated to fix this issue.
         */
        await this.sleep(400);
        this.isRendered = true;
    },
    computed: {
        ...mapState(useAuthStore, ['user']),
        /**
         * Get hours for digital time picker.
         * @return {Array} Array of formatted hours
         */
        getHoursOptions() {
            return this.getFormattedArray(23);
        },
        /**
         * Get minute intervals for analog and digital time picker.
         * @return {Array} Array of formatted minutes
         */
        getMinutesOptions() {
            const difference = parseInt(this.minuteInterval);
            return this.getFormattedArray(59, difference);
        },

        /**
         * Get seconds for digital time picker.
         * @return {Array} Array of formatted seconds
         */
        getSecondsOptions() {
            return this.getFormattedArray(59);
        },
        /**
         * Get formatted time with hour, minute, and seconds (00:00:00)
         * @return {String} Formatted time
         */
        time() {
            if (this.defaultTimePicker === 'analog') {
                return this.timePicker.analogTime;
            } else {
                return (
                    this.timePicker.digitalTime.hours +
                    ':' +
                    this.timePicker.digitalTime.minutes +
                    ':' +
                    this.timePicker.digitalTime.seconds
                );
            }
        },
        /**
         * Get default time picker
         * @return {String} default time picker
         */
        defaultTimePicker() {
            return this.user?.default_time_picker || 'digital';
        }
    },
    methods: {
        /**
         * Handles the time input value change, validates it, and updates the component's state.
         * @param {string} val - The new value from the input.
         */
        handleTimeInput(val) {
            this.timeInput = val;
            this.errorMessage = null;

            const time = val.split(':');
            const hours = time[0];
            const minutes = time[1];
            const seconds = time[2];

            if (this.withSeconds) {
                if (val.length !== 8) {
                    return;
                }
            } else {
                if (val.length !== 5) {
                    return;
                }
            }

            if (hours > 23 || minutes > 59 || seconds > 59) {
                this.errorMessage = 'Invalid time';
                return;
            }

            this.timePicker.digitalTime.hours = hours;
            this.timePicker.digitalTime.minutes = minutes;
            if (this.withSeconds) {
                this.timePicker.digitalTime.seconds = seconds;
            }
            this.timeChanged();
        },

        /**
         * Emits the updated time value to the parent component and updates the input field.
         */
        timeChanged() {
            // Update the timeInput value when the time is changed via the scroll picker
            if (this.defaultTimePicker !== 'analog') {
                this.timeInput = this.time;
                if (!this.withSeconds) {
                    // If seconds are not shown, truncate the timeInput to just hours and minutes
                    this.timeInput = this.timeInput.substring(0, 5);
                }
            }
            this.$emit('update:modelValue', this.time);
        },

        /**
         * Creates an array of numbers up to a maximum value, with each number
         * formatted as a zero-padded string.
         * @param {number} max - The maximum number in the array.
         * @param {number} [difference=1] - The interval between numbers. Defaults to 1.
         * @returns {string[]} An array of zero-padded numbers.
         */
        getFormattedArray(max, difference = 1) {
            const arr = [];
            // Check if the selected time picker is digital
            const isDigital = this.defaultTimePicker === 'digital';
            for (let i = 0; i <= max; i += difference) {
                if (isDigital) {
                    // Convert number to string with two digits
                    arr.push(i.toString().padStart(2, '0'));
                } else {
                    // The analog time picker expects the value to be a number,
                    arr.push(i);
                }
            }
            return arr;
        },

        /**
         * Sleep function used to render the vue-scroll-picker
         * @param {Number} ms - Milliseconds to sleep
         */
        async sleep(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        },

        /**
         * Prevents invalid time input by blocking keypresses that would result in an invalid time.
         * @param {Event} event - The keydown event object.
         */
        preventInvalidTimeInput(event) {
            const { key, target } = event;
            const { selectionStart, value } = target;

            const allowedKeys = [
                'Backspace',
                'ArrowLeft',
                'ArrowRight',
                'Tab',
                'Delete',
                'Home',
                'End'
            ];
            if (allowedKeys.includes(key) || event.ctrlKey || event.metaKey) {
                return;
            }

            if (!/\d/.test(key)) {
                event.preventDefault();
                return;
            }

            const digit = parseInt(key, 10);
            let isInvalid = false;

            // Mask: ##:##:## or ##:##
            // Indices: 0..1..3..4..6..7

            // Validate Hours
            if (selectionStart === 0 && digit > 2) {
                isInvalid = true;
            } else if (selectionStart === 1 && value[0] === '2' && digit > 3) {
                isInvalid = true;
            }
            // Validate Minutes
            else if (selectionStart === 3 && digit > 5) {
                isInvalid = true;
            }
            // Validate Seconds (if applicable)
            else if (this.withSeconds && selectionStart === 6 && digit > 5) {
                isInvalid = true;
            }

            if (isInvalid) {
                event.preventDefault();
            }
        }
    }
};
</script>

<style lang="scss" scoped>
:deep(.q-scroller) {
    background-color: white !important;
}
:deep(.q-scroller__item) {
    color: $secondary !important;
}
:deep(.q-scroller__item--selected) {
    color: $primary !important;
}
.scroll-picker {
    width: 120px;
    font-size: 18px;
    font-weight: bold;
}
.input-time-picker {
    max-width: 175px;
    font-size: 18px;
    font-weight: bold;
    margin: 20px auto 0 auto;
}
</style>
